import { type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import cx from 'clsx';
import { Button, Spinner } from '@repo/design-system-kit';
import { IconCertifiedFilled, IconCrossRoundedFilled } from '@repo/monochrome-icons';
import styles from './checkout-feedback.module.css';

interface CheckoutFeedbackProps {
  payee: string;
  isPolling?: boolean;
  hasNewPayment?: boolean;
  isError?: boolean;
  onRetry?: () => void;
}

export function CheckoutFeedback({
  payee,
  isPolling = false,
  hasNewPayment = false,
  isError = false,
  onRetry,
}: CheckoutFeedbackProps): ReactNode {
  if (isError) {
    return (
      <section className={styles.wrapper} data-test-checkout-feedback>
        <div className={styles.content} data-test-feedback-error>
          <IconCrossRoundedFilled color="#b81818" height={32} width={32} />
          <h2 className="title-2" data-testid="checkout-feedback-error-title">
            <FormattedMessage id="payment-link.payment.error.title" />
          </h2>
          <p className="body-2" data-testid="checkout-feedback-error-subtitle">
            <FormattedMessage
              id="payment-link.payment.error.subtitle"
              values={{ companyName: payee }}
            />
          </p>
          <Button data-testid="checkout-feedback-error-cta" onPress={onRetry}>
            <FormattedMessage id="payment-link.payment.error.cta" />
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.wrapper} data-test-checkout-feedback>
      {isPolling ? (
        <>
          <Spinner className="mb-24" color="primary-a" size="large" />
          <h2 className="title-2" data-testid="checkout-feedback-processing-title">
            <FormattedMessage id="payment-link.payment.processing" />
          </h2>
        </>
      ) : (
        <div className={cx(styles.content, 'mb-32')} data-test-feedback-success>
          <IconCertifiedFilled color="#0f7a3d" height={32} width={32} />
          {hasNewPayment ? (
            <>
              <h2 className="title-2" data-testid="checkout-feedback-success-title">
                <FormattedMessage id="payment-link.payment.success.title" />
              </h2>
              <p className="body-2">
                <FormattedMessage
                  id="payment-link.payment.success.subtitle"
                  values={{ companyName: payee }}
                />
              </p>
            </>
          ) : (
            <h2 className="title-2" data-testid="checkout-feedback-success-title">
              <FormattedMessage id="payment-link.payment.paid.title" />
            </h2>
          )}
        </div>
      )}
    </section>
  );
}
